import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  ApiList,
  School,
  StudyOption,
  Subject,
  Country,
  ApiObject,
  Program,
  Blog,
  Profile,
  User,
  CommonHash,
  Postulation,
  NextSteps,
  City,
  Landing,
  DocumentLead,
  Answer,
  PersonalStatementQuestion,
  PersonalStatement,
} from 'app/types';
import { API_URL } from 'common/api/client';
import type { RootState } from 'app/store';

const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers, { getState }) => {
    const profile_id = (getState() as RootState).session.profile?.id;
    const accessToken = (getState() as RootState).session.user?.accessToken;
    const backofficeToken = (getState() as RootState).backoffice.backofficeUser?.accessToken;
    if (profile_id) {
      headers.set('profile_id', `${profile_id}`);
    }
    if (accessToken) {
      headers.set('Authorization', `${accessToken}`);
    }
    if (backofficeToken) {
      headers.set('Authorization', backofficeToken);
    }

    return headers;
  },
});

export const exploreApi = createApi({
  reducerPath: 'exploreApi',
  baseQuery: baseQuery,
  tagTypes: [
    'Postulations',
    'Next Steps',
    'School',
    'Country',
    'Program',
    'City',
    'Programs',
    'Schools',
    'Personal Statement',
    'Personal Statements',
    'User',
    'Landings',
    'Subjects',
  ],
  endpoints: (builder) => ({
    getSchools: builder.query<ApiList<School>, any>({
      query: (params: any) => ({ url: 'schools', params }),
    }),
    getSchoolsPersonalStatement: builder.query<ApiList<School>, void>({
      query: () => 'schools/search',
    }),
    getSchoolProgramsPersonalStatement: builder.query<ApiList<Program>, number>({
      query: (id: number) => `schools/${id}/personal_statement_programs`,
    }),
    getSchool: builder.query<ApiObject<School>, any>({
      query: (params: any) => `schools/${params.id}?view=${params.view}`,
    }),
    getSchoolPrograms: builder.query<ApiList<Program>, any>({
      query: (params: any) => ({
        url: `schools/${params.id}/programs?page=${params.page}&subject=${params.subject}&degree_type=${params.degreeType}`,
      }),
    }),
    getSchoolSimilars: builder.query<ApiObject<ApiList<School>>, number>({
      query: (id: number) => `schools/${id}/similar_schools`,
    }),
    getStudyOptions: builder.query<ApiList<StudyOption>, any>({
      query: (params: any) => ({ url: 'study_options', params }),
    }),
    getStudyOption: builder.query<ApiObject<StudyOption>, number>({
      query: (id: number) => `study_options/${id}`,
    }),
    getStudyOptionSimilars: builder.query<ApiObject<ApiList<StudyOption>>, number>({
      query: (id: number) => `study_options/${id}/similar_programs`,
    }),
    getSubjects: builder.query<ApiList<Subject>, any>({
      query: (params: any) => ({ url: 'subjects', params }),
    }),
    getCountries: builder.query<ApiList<Country>, any>({
      query: (params: any) => ({ url: 'countries', params }),
    }),
    getSearchBarCountries: builder.query<ApiList<Country>, void>({
      query: () => ({ url: 'countries/search_bar' }),
    }),
    getCountry: builder.query<ApiObject<Country>, number>({
      query: (id: number) => `countries/${id}`,
    }),
    getPartners: builder.query<ApiList<School>, void>({
      query: () => `schools?partners=true`,
    }),
    getIntakeMonths: builder.query<{ data: number[] }, void>({
      query: () => `intake_dates/months`,
    }),
    getPrograms: builder.query<ApiList<Program>, any>({
      query: (params: any) => ({ url: 'programs', params }),
      providesTags: ['Programs'],
    }),
    getProgram: builder.query<ApiObject<Program>, any>({
      query: (params: any) => ({ url: `programs/${params.program_id}`, params }),
    }),
    getProgramSimilars: builder.query<ApiObject<ApiList<Program>>, number>({
      query: (id: number) => `programs/${id}/similar_programs`,
    }),
    getProgramStudyOptions: builder.query<ApiObject<Program>, number>({
      // revisar este return
      query: (id: number) => `programs/${id}/study_options`,
    }),
    getLocationsForFilter: builder.query<
      { data: { type: string; value: number; label: string }[] },
      { query: string; cities?: number[]; countries?: number[] }
    >({
      query: ({ query, cities, countries }) =>
        `programs/locations?query=${query || ''}${
          cities?.map((i) => `&cities[]=${i}`).join() || ''
        }${countries?.map((i) => `&countries[]=${i}`).join() || ''}`,
    }),
    getSubjectsForFilter: builder.query<
      { data: { type: string; value: number; label: string }[] },
      { query: string; ids?: number[] }
    >({
      query: ({ query, ids }) =>
        `programs/subjects?query=${query || ''}${ids?.map((i) => `&ids[]=${i}`).join() || ''}`,
    }),
    getSchoolsForFilter: builder.query<
      { data: { type: string; value: number; label: string }[] },
      { query: string; ids?: number[] }
    >({
      query: ({ query, ids }) =>
        `schools/filter?query=${query || ''}${ids?.map((i) => `&ids[]=${i}`).join() || ''}`,
    }),
    getBlogEntries: builder.query<ApiList<Blog>, void>({
      query: () => `blog`,
    }),
    getAllCountries: builder.query<ApiList<Country>, void>({
      query: () => `countries/for_profile`,
    }),
    getAllSubjects: builder.query<ApiList<Subject>, void>({
      query: () => `subjects/for_profile`,
    }),
    getFavorites: builder.query<ApiList<School | Program>, string>({
      query: (kind) => `users/favorites?kind=${kind}`,
    }),
    getFavoritesAmount: builder.query<CommonHash, void>({
      query: () => '/favorites',
    }),
    getBackofficeUserFavorites: builder.query<ApiList<Program>, number>({
      query: (id) => `backoffice/users/${id}/favorites`,
    }),
    addProfile: builder.mutation<ApiObject<Profile>, Partial<Profile>>({
      query(body) {
        return {
          url: `profiles`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['Programs'],
    }),
    getProfile: builder.query<ApiObject<Profile>, string>({
      query: (email: string) => `profiles/by_email?email=${email}`,
    }),
    addSession: builder.mutation<{ response: User; auth: string }, CommonHash>({
      query(body) {
        return {
          url: `users`,
          method: 'POST',
          body,
        };
      },
      transformResponse(response: any, meta: any) {
        return {
          response: response.data,
          auth: meta.response.headers.get('Authorization'),
        };
      },
    }),
    addLogin: builder.mutation<{ response: User; auth: string }, CommonHash>({
      query(body) {
        return {
          url: `users/sign_in`,
          method: 'POST',
          body,
        };
      },
      transformResponse(response: any, meta: any) {
        return {
          response: response.data,
          auth: meta.response.headers.get('Authorization'),
        };
      },
    }),
    addBackofficeLogin: builder.mutation<{ response: User; auth: string }, CommonHash>({
      query(body) {
        return {
          url: `backoffice/login`,
          method: 'POST',
          body,
        };
      },
      transformResponse(response: any, meta: any) {
        return {
          response: response,
          auth: meta.response.headers.get('Authorization'),
        };
      },
    }),
    addFavorite: builder.mutation<any, any>({
      query(body) {
        return {
          url: 'users/add_favorite',
          method: 'POST',
          body,
        };
      },
    }),
    deleteFavorite: builder.mutation<any, any>({
      query(body) {
        return {
          url: 'users/delete_favorite',
          method: 'POST',
          body,
        };
      },
    }),
    submitExternalForm: builder.mutation<any, any>({
      query() {
        return {
          url: 'users/submit_external_form',
          method: 'POST',
        };
      },
    }),
    removeLogin: builder.mutation<void, void>({
      query() {
        return {
          url: `users/sign_out`,
          method: 'DELETE',
        };
      },
      // invalidatesTags: ['Next Steps'],
    }),
    getUser: builder.query<ApiObject<User>, string>({
      query: (token: string) => `users/login_by_google?token=${token}`,
    }),
    getPostulation: builder.query<ApiObject<Postulation>, number>({
      query: (id: number) => `postulations/${id}`,
    }),
    getBackofficePostulation: builder.query<ApiObject<Postulation>, string>({
      query: (id: string) => `backoffice/postulations/${id}`,
    }),
    getPostulationsList: builder.query<CommonHash, void>({
      query: () => `postulations`,
      providesTags: ['Postulations'],
    }),
    getPostulations: builder.query<CommonHash, void>({
      query: () => `application_list`,
      providesTags: ['Postulations'],
    }),
    addPostulation: builder.mutation<void, any>({
      query(body) {
        return {
          url: 'postulations',
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['Postulations'],
    }),
    addBackofficePostulation: builder.mutation<void, any>({
      query(body) {
        return {
          url: 'backoffice/postulations',
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['Postulations'],
    }),
    getBackofficeUserPostulations: builder.query<CommonHash, string>({
      query: (id) => `backoffice/${id}/application_list`,
      providesTags: ['Postulations'],
    }),
    updatePostulation: builder.mutation<ApiObject<Postulation>, { id: number; data: FormData }>({
      query: ({ id, data }) => ({
        url: `postulations/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Postulations'],
    }),
    updateBackofficePostulation: builder.mutation<
      ApiObject<Postulation>,
      { id: number; data: FormData }
    >({
      query: ({ id, data }) => ({
        url: `backoffice/postulations/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Postulations'],
    }),
    updateBackofficePostulationStatus: builder.mutation<
      ApiObject<Postulation>,
      { id: number; data: CommonHash }
    >({
      query: ({ id, data }) => ({
        url: `backoffice/postulations/${id}/update_status`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Postulations'],
    }),
    updateUser: builder.mutation<ApiObject<User>, { data: FormData; id: number }>({
      query: ({ id, data }) => ({
        url: `users/${id}`,
        method: 'PATCH',
        body: data,
        invalidatesTags: ['User'],
      }),
    }),
    updateBackofficeUser: builder.mutation<ApiObject<User>, { data: FormData; id: number }>({
      query: ({ id, data }) => ({
        url: `backoffice/users/${id}`,
        method: 'PATCH',
        body: data,
        invalidatesTags: ['User'],
      }),
    }),
    updateNextSteps: builder.mutation<any, any>({
      query(body) {
        return {
          url: 'users/update_next_steps',
          method: 'PATCH',
          body,
        };
      },
    }),
    getNextSteps: builder.query<ApiObject<NextSteps>, void>({
      query: () => `users/next_steps`,
      keepUnusedDataFor: 5,
      // providesTags: ['Next Steps'],
    }),
    deleteAttachment: builder.mutation<void, { file_type: string; index: number | undefined }>({
      query: (body) => ({
        url: `users/delete_attachment`,
        method: 'DELETE',
        body: body,
      }),
    }),
    deleteBackofficeAttachment: builder.mutation<
      void,
      { id: number; file_type: string; index: number | undefined }
    >({
      query: (body) => ({
        url: `backoffice/users/${body.id}/delete_attachment`,
        method: 'DELETE',
        body: body,
      }),
    }),
    deleteHistory: builder.mutation<void, { index: number; type: string }>({
      query: ({ index, type }) => ({
        url: `users/delete_history?index=${index}&type=${type}`,
        method: 'DELETE',
      }),
    }),
    deleteBackofficeHistory: builder.mutation<void, { id: number; index: number; type: string }>({
      query: ({ id, index, type }) => ({
        url: `backoffice/users/${id}/delete_history?index=${index}&type=${type}`,
        method: 'DELETE',
      }),
    }),
    deleteUser: builder.mutation<void, number>({
      query: (id) => ({
        url: `users/${id}`,
        method: 'DELETE',
      }),
    }),
    addPostulationFiles: builder.mutation<void, { data: FormData; id: number }>({
      query: ({ id, data }) => ({
        url: `postulations/${id}/upload_files`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Postulations'],
    }),
    addBackofficePostulationFiles: builder.mutation<void, { data: FormData; id: number }>({
      query: ({ id, data }) => ({
        url: `backoffice/postulations/${id}/upload_files`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Postulations'],
    }),
    getFavoritePostulation: builder.query<ApiObject<Postulation>, number>({
      query: (id) => ({
        url: `favorites/postulation/${id}`,
        method: 'GET',
      }),
    }),
    getBackofficeFavoritePostulation: builder.query<
      ApiObject<Postulation>,
      { program_id: number; id: number }
    >({
      query: ({ program_id, id }) => ({
        url: `backoffice/favorites/postulation/${program_id}`,
        method: 'GET',
        params: { id },
      }),
    }),
    getBackofficeSchool: builder.query<any, number>({
      query: (id) => ({
        url: `backoffice/schools/${id}`,
        method: 'GET',
      }),
      providesTags: ['School'],
    }),
    getBackofficeProgram: builder.query<ApiObject<Program>, number>({
      query: (id) => ({
        url: `backoffice/programs/${id}`,
        method: 'GET',
      }),
      providesTags: ['Program'],
    }),
    getBackofficePrograms: builder.query<ApiList<Program>, any>({
      query: (params: any) => ({ url: 'backoffice/programs', params }),
      providesTags: ['Programs'],
    }),
    getAllBackofficePrograms: builder.query<ApiList<Program>, any>({
      query: (params: any) => ({ url: 'backoffice/programs/search', params }),
      providesTags: ['Programs'],
    }),
    getBackofficeSchools: builder.query<ApiList<School>, any>({
      query: (params: any) => ({ url: 'backoffice/schools', params }),
      providesTags: ['Schools'],
    }),
    getBackofficeNewProgram: builder.query<ApiObject<Program>, void>({
      query: () => 'backoffice/programs/new',
    }),
    getBackofficeNewSchool: builder.query<ApiObject<School>, void>({
      query: () => 'backoffice/schools/new',
    }),
    getBackofficeNewLanding: builder.query<ApiObject<Landing>, void>({
      query: () => 'backoffice/landings/new',
    }),
    addProgram: builder.mutation<ApiObject<Program>, Partial<Program>>({
      query(body) {
        return {
          url: `/backoffice/programs`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['Programs'],
    }),
    addSchool: builder.mutation<ApiObject<School>, Partial<School>>({
      query(body) {
        return {
          url: `/backoffice/schools`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['Schools'],
    }),
    addLanding: builder.mutation<ApiObject<Landing>, { data: FormData }>({
      query({ data }) {
        return {
          url: `/backoffice/landings`,
          method: 'POST',
          body: data,
        };
      },
      invalidatesTags: ['Landings'],
    }),
    getBackofficeCountries: builder.query<ApiList<Country>, any>({
      query: (params: any) => ({ url: 'backoffice/countries', params }),
    }),
    getBackofficeCities: builder.query<ApiList<City>, any>({
      query: (params: any) => ({ url: 'backoffice/cities', params }),
    }),
    getBackofficeCountry: builder.query<any, number>({
      query: (id) => ({
        url: `backoffice/countries/${id}`,
        method: 'GET',
      }),
    }),
    getBackofficeCity: builder.query<any, number>({
      query: (id) => ({
        url: `backoffice/cities/${id}`,
        method: 'GET',
      }),
    }),
    addSchoolAttachment: builder.mutation<void, { school_id: string; data: FormData }>({
      query: ({ school_id, data }) => ({
        url: `backoffice/schools/${school_id}/add_attachments`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['School'],
    }),
    removeSchoolAttachment: builder.mutation<void, { id: string; data: CommonHash }>({
      query: ({ id, data }) => ({
        url: `backoffice/schools/${id}/delete_attachments`,
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: ['School'],
    }),
    addCountryAttachment: builder.mutation<void, { country_id: string; data: FormData }>({
      query: ({ country_id, data }) => ({
        url: `backoffice/countries/${country_id}/add_attachments`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Country'],
    }),
    removeCountryAttachment: builder.mutation<void, { id: string; data: CommonHash }>({
      query: ({ id, data }) => ({
        url: `backoffice/countries/${id}/delete_attachments`,
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: ['Country'],
    }),
    addProgramAttachment: builder.mutation<void, { program_id: string; data: FormData }>({
      query: ({ program_id, data }) => ({
        url: `backoffice/programs/${program_id}/add_attachments`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Program'],
    }),
    removeProgramAttachment: builder.mutation<void, { id: string; data: CommonHash }>({
      query: ({ id, data }) => ({
        url: `backoffice/programs/${id}/delete_attachments`,
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: ['Program'],
    }),
    addCityAttachment: builder.mutation<void, { city_id: string; data: FormData }>({
      query: ({ city_id, data }) => ({
        url: `backoffice/cities/${city_id}/add_attachments`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['City'],
    }),
    removeCityAttachment: builder.mutation<void, { id: string; data: CommonHash }>({
      query: ({ id, data }) => ({
        url: `backoffice/cities/${id}/delete_attachments`,
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: ['City'],
    }),
    getSchoolsSearch: builder.query<ApiList<CommonHash>, void>({
      query: () => 'backoffice/schools/search',
    }),
    getCitiesSearch: builder.query<ApiList<CommonHash>, void>({
      query: () => 'backoffice/cities/search',
    }),
    getTags: builder.query<ApiList<CommonHash>, void>({
      query: () => 'backoffice/tags',
    }),
    patchProgram: builder.mutation<void, { program_id: string; data: CommonHash }>({
      query: ({ program_id, data }) => ({
        url: `backoffice/programs/${program_id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Program'],
    }),
    getSchoolEdit: builder.query<ApiObject<School>, number>({
      query: (id) => ({
        url: `backoffice/schools/${id}/edit`,
        method: 'GET',
      }),
      providesTags: ['School'],
    }),
    patchSchool: builder.mutation<void, { school_id: string; data: CommonHash }>({
      query: ({ school_id, data }) => ({
        url: `backoffice/schools/${school_id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['School'],
    }),
    deleteSchool: builder.mutation<void, number>({
      query: (id) => ({
        url: `backoffice/schools/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Schools'],
    }),
    deleteProgram: builder.mutation<void, number>({
      query: (id) => ({
        url: `backoffice/programs/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Programs'],
    }),
    getLanding: builder.query<ApiObject<Landing>, string>({
      query: (slug) => ({
        url: `landings/${slug}`,
        method: 'GET',
      }),
    }),
    getBackofficeDocumentLeads: builder.query<ApiList<DocumentLead>, any>({
      query: (params: any) => ({ url: 'backoffice/document_leads', params }),
    }),
    getBackofficeDocumentLeadsCSV: builder.query<any, any>({
      query: (params: any) => ({
        url: 'backoffice/document_leads/download_csv',
        params,
        responseHandler: async (response) => {
          const blob = await response.blob();
          const fileURL = URL.createObjectURL(blob);
          return fileURL;
        },
      }),
    }),
    getSchoolAnswers: builder.query<ApiList<Answer>, number>({
      query: (id) => ({
        url: `schools/${id}/answers`,
        method: 'GET',
      }),
    }),
    getPersonalStatementQuestions: builder.query<ApiList<PersonalStatementQuestion>, any>({
      query: (params: any) => ({
        url: `personal_statements/questions`,
        params,
      }),
    }),
    addPersonalStatement: builder.mutation<void, void>({
      query() {
        return {
          url: 'personal_statements',
          method: 'POST',
        };
      },
      invalidatesTags: ['Personal Statements'],
    }),
    patchPersonalStatement: builder.mutation<void, { id: number; data: CommonHash }>({
      query: ({ id, data }) => ({
        url: `personal_statements/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Personal Statements', 'Personal Statement'],
    }),
    deletePersonalStatement: builder.mutation<void, number>({
      query: (id) => ({
        url: `personal_statements/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Personal Statements', 'Personal Statement'],
    }),
    duplicatePersonalStatement: builder.mutation<void, number>({
      query(id) {
        return {
          url: `personal_statements/${id}/duplicate`,
          method: 'POST',
        };
      },
      invalidatesTags: ['Personal Statements'],
    }),
    generatePersonalStatement: builder.mutation<void, { id: number; data: CommonHash }>({
      query: ({ id, data }) => ({
        url: `personal_statements/${id}/generate`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Personal Statements'],
    }),
    sendPersonalStatementMailFile: builder.mutation<void, { data: FormData; id: number }>({
      query: ({ id, data }) => ({
        url: `personal_statements/${id}/send_cx_mail`,
        method: 'POST',
        body: data,
      }),
    }),
    getPersonalStatement: builder.query<ApiObject<PersonalStatement>, number>({
      query: (id) => ({
        url: `personal_statements/${id}`,
        method: 'GET',
      }),
      providesTags: ['Personal Statement'],
    }),
    getPersonalStatements: builder.query<ApiList<PersonalStatement>, void>({
      query: () => ({
        url: `personal_statements`,
        method: 'GET',
      }),
      providesTags: ['Personal Statements'],
    }),
    updateUserFromPersonalStatement: builder.mutation<ApiObject<User>, { data: CommonHash }>({
      query: ({ data }) => ({
        url: `users/update_user_from_personal_statement`,
        method: 'PATCH',
        body: data,
      }),
    }),
    getBackofficeUsers: builder.query<ApiList<User>, any>({
      query: (params: any) => ({ url: 'backoffice/users', params }),
    }),
    getBackofficeUser: builder.query<ApiObject<User>, string>({
      query: (id) => ({ url: `backoffice/users/${id}` }),
      providesTags: ['User'],
      keepUnusedDataFor: 0,
    }),
    getBackofficePostulations: builder.query<ApiList<Postulation>, any>({
      query: (params: any) => ({ url: 'backoffice/postulations', params }),
    }),
    getBackofficePostulationStatus: builder.query<ApiObject<Postulation>, string>({
      query: (id: string) => `backoffice/postulations/${id}/status`,
    }),
    getBackofficeSchoolPrograms: builder.query<ApiList<Program>, number>({
      query: (id: number) => `backoffice/schools/${id}/programs`,
    }),
    getBackofficeLandings: builder.query<ApiList<Landing>, any>({
      query: (params: any) => ({ url: 'backoffice/landings', params }),
      providesTags: ['Landings'],
    }),
    getBackofficeEditLanding: builder.query<ApiObject<Landing>, string>({
      query: (id) => ({ url: `backoffice/landings/${id}/edit` }),
    }),
    getBackofficeLanding: builder.query<ApiObject<Landing>, string>({
      query: (id) => ({ url: `backoffice/landings/${id}` }),
    }),
    getHomePageLanding: builder.query<ApiObject<Landing>, void>({
      query: () => ({ url: `landings/home_page` }),
    }),
    getBackofficeSubjects: builder.query<ApiList<Subject>, any>({
      query: (params: any) => ({ url: 'backoffice/subjects', params }),
      providesTags: ['Subjects'],
    }),
    addSubject: builder.mutation<ApiObject<Subject>, Partial<Subject>>({
      query(body) {
        return {
          url: `backoffice/subjects`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['Subjects'],
    }),
    patchSubject: builder.mutation<void, { id: number; data: CommonHash }>({
      query: ({ id, data }) => ({
        url: `backoffice/subjects/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Subjects'],
    }),
    getBackofficeNewSubject: builder.query<ApiObject<Subject>, void>({
      query: () => 'backoffice/subjects/new',
    }),
    getBackofficeEditSubject: builder.query<ApiObject<Subject>, string>({
      query: (id) => ({ url: `backoffice/subjects/${id}/edit` }),
      providesTags: ['Subjects'],
    }),
    deleteSubjectAttachment: builder.mutation<void, string>({
      query: (id) => ({
        url: `backoffice/subjects/${id}/delete_attachment`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Subjects'],
    }),
    submitRequestMeetingForm: builder.mutation<void, CommonHash>({
      query: (data) => ({
        url: 'leads/request_meeting',
        method: 'POST',
        body: data,
      }),
    }),
    submitGenericForm: builder.mutation<void, CommonHash>({
      query: (data) => ({
        url: 'leads/generic_form',
        method: 'POST',
        body: data,
      }),
    }),
  }),
});
export const {
  useGetSchoolsQuery,
  useGetSchoolQuery,
  useGetSchoolProgramsQuery,
  useGetSchoolSimilarsQuery,
  useLazyGetSubjectsQuery,
  useGetSubjectsQuery,
  useGetCountriesQuery,
  useLazyGetCountriesQuery,
  useGetSearchBarCountriesQuery,
  useGetCountryQuery,
  useGetPartnersQuery,
  useLazyGetPartnersQuery,
  useLazyGetIntakeMonthsQuery,
  useLazyGetLocationsForFilterQuery,
  useLazyGetSubjectsForFilterQuery,
  useLazyGetSchoolsForFilterQuery,
  useGetProgramsQuery,
  useGetFavoritesQuery,
  useGetProgramQuery,
  useGetProgramSimilarsQuery,
  useGetProgramStudyOptionsQuery,
  useGetBlogEntriesQuery,
  useLazyGetBlogEntriesQuery,
  useGetAllCountriesQuery,
  useGetAllSubjectsQuery,
  useAddProfileMutation,
  useGetProfileQuery,
  useAddSessionMutation,
  useAddLoginMutation,
  useAddFavoriteMutation,
  useDeleteFavoriteMutation,
  useRemoveLoginMutation,
  useGetUserQuery,
  useGetPostulationQuery,
  useLazyGetPostulationsQuery,
  useAddPostulationMutation,
  useGetPostulationsQuery,
  useUpdatePostulationMutation,
  useUpdateUserMutation,
  useDeleteAttachmentMutation,
  useDeleteHistoryMutation,
  useSubmitExternalFormMutation,
  useDeleteUserMutation,
  useAddPostulationFilesMutation,
  useGetFavoritePostulationQuery,
  useAddBackofficeLoginMutation,
  useUpdateNextStepsMutation,
  useGetNextStepsQuery,
  useGetBackofficeSchoolQuery,
  useGetBackofficeCountriesQuery,
  useGetBackofficeCountryQuery,
  useAddSchoolAttachmentMutation,
  useRemoveSchoolAttachmentMutation,
  useAddCountryAttachmentMutation,
  useRemoveCountryAttachmentMutation,
  useAddProgramAttachmentMutation,
  useRemoveProgramAttachmentMutation,
  useGetBackofficeProgramQuery,
  useGetBackofficeCitiesQuery,
  useGetBackofficeCityQuery,
  useAddCityAttachmentMutation,
  useRemoveCityAttachmentMutation,
  useGetBackofficeProgramsQuery,
  useGetAllBackofficeProgramsQuery,
  useLazyGetBackofficeProgramsQuery,
  useGetBackofficeNewProgramQuery,
  useGetBackofficeNewLandingQuery,
  useAddLandingMutation,
  useAddProgramMutation,
  useGetSchoolsSearchQuery,
  usePatchProgramMutation,
  useGetBackofficeNewSchoolQuery,
  useAddSchoolMutation,
  usePatchSchoolMutation,
  useGetBackofficeSchoolsQuery,
  useGetCitiesSearchQuery,
  useGetSchoolEditQuery,
  useDeleteSchoolMutation,
  useDeleteProgramMutation,
  useLazyGetProgramsQuery,
  useLazyGetSchoolsQuery,
  useLazyGetSchoolProgramsPersonalStatementQuery,
  useGetLandingQuery,
  useGetTagsQuery,
  useGetBackofficeDocumentLeadsQuery,
  useGetSchoolAnswersQuery,
  useGetBackofficeDocumentLeadsCSVQuery,
  useAddPersonalStatementMutation,
  useDuplicatePersonalStatementMutation,
  useGetSchoolsPersonalStatementQuery,
  useGetPersonalStatementQuestionsQuery,
  usePatchPersonalStatementMutation,
  useGeneratePersonalStatementMutation,
  useSendPersonalStatementMailFileMutation,
  useGetPersonalStatementQuery,
  useGetPersonalStatementsQuery,
  useDeletePersonalStatementMutation,
  useUpdateUserFromPersonalStatementMutation,
  useGetPostulationsListQuery,
  useLazyGetPostulationsListQuery,
  useGetBackofficeUsersQuery,
  useGetBackofficeUserQuery,
  useGetBackofficeUserPostulationsQuery,
  useGetBackofficeUserFavoritesQuery,
  useGetBackofficeFavoritePostulationQuery,
  useAddBackofficePostulationMutation,
  useUpdateBackofficePostulationMutation,
  useGetBackofficePostulationQuery,
  useAddBackofficePostulationFilesMutation,
  useDeleteBackofficeAttachmentMutation,
  useDeleteBackofficeHistoryMutation,
  useUpdateBackofficeUserMutation,
  useGetBackofficePostulationsQuery,
  useUpdateBackofficePostulationStatusMutation,
  useGetBackofficePostulationStatusQuery,
  useLazyGetBackofficeSchoolProgramsQuery,
  useGetBackofficeLandingsQuery,
  useGetBackofficeEditLandingQuery,
  useGetBackofficeLandingQuery,
  useGetHomePageLandingQuery,
  useGetFavoritesAmountQuery,
  useLazyGetSchoolQuery,
  useGetBackofficeSubjectsQuery,
  useAddSubjectMutation,
  usePatchSubjectMutation,
  useGetBackofficeNewSubjectQuery,
  useGetBackofficeEditSubjectQuery,
  useDeleteSubjectAttachmentMutation,
  useSubmitRequestMeetingFormMutation,
  useSubmitGenericFormMutation,
} = exploreApi;
